import React, {
  useState,
  useEffect,
  Suspense,
  lazy,
  useCallback,
} from 'react';
import {
  Link,
  useNavigate,
  useParams,
  useLocation,
} from 'react-router-dom';

import api from '../api';
import { isEditLocked } from '../utils/isEditLocked';

import '../styles/ParameterChoice.css';
import '../styles/buttons.css';

const TagsMatching = lazy(() => import('./TagsMatching'));
const Distance = lazy(() => import('./Distance'));

const ParameterChoice = ({
  mock = false,
  user_id: injectedId,
  frameId,
}) => {
  const { user_id: urlId } = useParams();
  const user_id = injectedId || urlId;
  const navigate = useNavigate();
  const location = useLocation();

  const [activeStep, setActiveStep] = useState(0);
  const [accountStatus, setAccountStatus] = useState(null);
  const [selectedVenues, setSelectedVenues] = useState([]);
  const [tagFilteredVenues, setTagFilteredVenues] = useState([]);
  const [storageAccessNeeded, setStorageAccessNeeded] = useState(false);

  // If we came here with state.selectedVenues, load them on mount
  useEffect(() => {
    if (
      location.state &&
      Array.isArray(location.state.selectedVenues)
    ) {
      setSelectedVenues(location.state.selectedVenues);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  useEffect(() => {
    if (accountStatus && isEditLocked(accountStatus)) {
      navigate(`/dashboard/${user_id}`);
    }
  }, [accountStatus, navigate, user_id]);

  const stepsCount = 3;
  const progressPercentage = ((activeStep + 1) / stepsCount) * 100;

  const fetchAccountStatus = useCallback(() => {
    if (mock) {
      setAccountStatus({ subscription: { plan: 'premium', credits_remaining: 99 } });
      return;
    }

    api
      .get('/account_status/', { params: { user_id } })
      .then(res => {
        if (res.data.status === 'success') {
          setAccountStatus(res.data.account_status);
        } else {
          console.error('Error fetching account status:', res.data.message);
        }
      })
      .catch(err => console.error('Error during fetchAccountStatus:', err));
  }, [mock, user_id]);

  useEffect(() => {
    const hasSA =
      typeof document.requestStorageAccess === 'function' &&
      typeof document.hasStorageAccess === 'function';

    if (hasSA) {
      document
        .hasStorageAccess()
        .then(has => {
          if (has) {
            fetchAccountStatus();
          } else {
            setStorageAccessNeeded(true);
          }
        })
        .catch(() => setStorageAccessNeeded(true));
    } else {
      if (
        navigator.userAgent.includes('Safari') &&
        !navigator.userAgent.includes('Chrome')
      ) {
        alert(
          'Please enable cookies/cross-site tracking in Safari settings.'
        );
      }
      fetchAccountStatus();
    }
  }, [fetchAccountStatus]);

  const handleRequestStorageAccess = () => {
    if (typeof document.requestStorageAccess === 'function') {
      document
        .requestStorageAccess()
        .then(() => {
          setStorageAccessNeeded(false);
          fetchAccountStatus();
        })
        .catch(() => alert('Please enable storage access.'));
    } else {
      alert(
        'Storage Access API not supported; please ensure cookies are enabled.'
      );
    }
  };

  const handleNextStep = () => {
    if (activeStep === 0) {
      if (tagFilteredVenues.length === 0) {
        alert('Select some tags first.');
        return;
      }
      setActiveStep(1);
      return;
    }

    if (activeStep === 1) {
      navigate(`/preferences/${user_id}`, {
        state: { selectedVenues },
      });
    }
  };

  const handleBack = () => {
    if (activeStep === 1) {
      setActiveStep(0);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeStep]);

  return (
    <div className="parameters-main-container" id={frameId}>
      <header className="logo">
        <Link to={`/dashboard/${user_id}`}>BestDay2Marry</Link>
      </header>
      <div className="parameter-choice-page-container">
        {storageAccessNeeded && (
          <div className="storage-access-request">
            <p>Enable storage access for best experience:</p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleRequestStorageAccess}
            >
              Enable Storage Access
            </button>
          </div>
        )}

        <div className="parameter-choice-container">
          <div className="progress-bar-container">
            <div
              className="progress-bar-fill"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>

          <Suspense fallback={<div>Loading step...</div>}>
            {activeStep === 0 && (
              <TagsMatching
                user_id={user_id}
                accountStatus={accountStatus}
                selectedVenues={selectedVenues}
                setSelectedVenues={setSelectedVenues}
                setTagFilteredVenues={setTagFilteredVenues}
              />
            )}
            {activeStep === 1 && (
              <Distance
                user_id={user_id}
                accountStatus={accountStatus}
                selectedVenues={selectedVenues}
                setSelectedVenues={setSelectedVenues}
                tagFilteredVenues={tagFilteredVenues}
              />
            )}
          </Suspense>

          <div className="sticky-footer">
            {activeStep === 0 && (
              <div className="tooltip-container">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() =>
                    navigate(`/find_venues/${user_id}`, {
                      state: { selectedVenues },
                    })
                  }
                >
                  Back to Map
                </button>
                <div role="tooltip" className="tooltip-text">
                  Want to return to the map to select venues? Click here!
                </div>
              </div>
            )}

            {activeStep === 1 && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleBack}
              >
                Back
              </button>
            )}

            {activeStep === 0 && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleNextStep}
              >
                Next: Distance
              </button>
            )}

            {activeStep === 1 && (
              <div className="tooltip-container">
                <button
                  type="button"
                  className={`btn btn-primary${
                    selectedVenues.length === 0 ? ' btn-disabled' : ''
                  }`}
                  onClick={
                    selectedVenues.length === 0 ? undefined : handleNextStep
                  }
                  disabled={selectedVenues.length === 0}
                >
                  Next: Preferences
                </button>
                {selectedVenues.length === 0 && (
                  <div role="tooltip" className="tooltip-text">
                    You need to select at least one venue to continue with the matching process.
                  </div>
                )}
              </div>
            )}

            {activeStep === 0 && selectedVenues.length > 0 && (
              <div className="tooltip-container">
                <button
                  type="button"
                  className="btn btn-primary"
                  aria-label="Skip distance and set preferences"
                  onClick={() =>
                    navigate(`/preferences/${user_id}`, {
                      state: { selectedVenues },
                    })
                  }
                >
                  Go to date matching (I’ve chosen a venue) →
                </button>
                <div
                  role="tooltip"
                  id="skip-tooltip"
                  className="tooltip-text"
                >
                  Already found your dream venue? Click here to go straight to
                  finding date matches!
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParameterChoice;
